class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]"
    );
    this.defaultContent = this.predictiveSearchResults.innerHTML;
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";

    // Get the enable_live_search setting from the form's data attribute or section data
    this.enableLiveSearch =
      this.getAttribute("data-enable-live-search") === "true";

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.input.addEventListener("click", this.onFocus.bind(this));
    this.input.addEventListener("input", this.onChange.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));

    // Add scroll and resize listeners for fixed positioning
    window.addEventListener("scroll", this.onScroll.bind(this));
    window.addEventListener("resize", this.onResize.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      // Check if any search settings are configured before showing default content
      const hasSearchSettings =
        window.themeSearchData?.hasSearchSettings || false;

      if (this.enableLiveSearch && hasSearchSettings) {
        // Show default content when search is cleared, but keep flydown open if live search is enabled
        this.showDefaultContent();
        this.open();
      } else {
        // Close if live search is not enabled or no search settings are configured
        this.close(true);
      }
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      // Check if any search settings are configured before showing default content
      const hasSearchSettings =
        window.themeSearchData?.hasSearchSettings || false;

      // If live search is enabled and search settings are configured, show default content on focus
      if (this.enableLiveSearch && hasSearchSettings) {
        this.showDefaultContent();
        this.open();
      }
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    // Don't automatically close on empty query - let onChange handle it properly
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  onScroll() {
    // Update position when scrolling if search is open
    if (this.isOpen) {
      this.updatePosition();
    }
  }

  onResize() {
    // Update position and max height when window is resized
    if (this.isOpen) {
      this.updatePosition();
    }
  }

  updatePosition() {
    // Try to find the search input to position relative to it
    const searchInput = this.input;
    let topPosition = 80; // fallback

    if (searchInput) {
      const inputRect = searchInput.getBoundingClientRect();
      topPosition = inputRect.bottom;
    } else {
      // Fallback to header calculation
      const header = document.querySelector(".section-header");
      if (header) {
        const headerRect = header.getBoundingClientRect();
        topPosition = headerRect.bottom;
      }
    }

    const searchFlydown = this.querySelector(".search-flydown");
    if (searchFlydown) {
      searchFlydown.style.setProperty("--header-height", `${topPosition}px`);
    }

    // Update max height
    this.predictiveSearchResults.style.maxHeight = `${
      window.innerHeight - topPosition
    }px`;
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${window.Theme.routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search&resources[options][fields]=title,product_type,variants.title,variants.sku`,
      {
        signal: this.abortController.signal,
      }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderTrendingKeywords(trending) {
    const trendingEl = this.querySelector(".search-flydown--trending");
    const trendingSection = this.querySelector(
      ".search-flydown-trending-section"
    );

    // Only try to render trending if the section exists
    if (!trendingSection) {
      return;
    }

    // If we have static content (elements already exist), show the section
    if (trendingEl && trendingEl.children.length > 0) {
      trendingSection.style.display = "block";
    }
    // If no static content but we have trending data, generate it dynamically
    else if (trendingEl && trending.length > 0) {
      trendingSection.style.display = "block";

      let trendingHTML = `
        ${trending
          .map(
            (item) =>
              `<a href="/search?q=${encodeURIComponent(
                item
              )}" class="trending-pill">${item}</a>`
          )
          .join("")}
      `;

      trendingEl.innerHTML = trendingHTML;
    }
    // If no trending data, hide the trending section but don't return early
    else {
      trendingSection.style.display = "none";
    }
  }

  renderPopularSuggestions(popularSearches) {
    // Only render if we have popular searches configured
    if (!popularSearches || popularSearches.length === 0) {
      return "";
    }

    const popularHTML = `
      <div class="search-flydown-content" id="predictive-search-results-wrapper">
        <!-- Trending Pills Section (Full Width) - initially hidden, shown by JavaScript if trending data exists -->
        <div class="search-flydown-trending-section" style="display: none;">
          <h3 class="search-flydown-section-title">TRENDING</h3>
          <div class="search-flydown--trending"></div>
        </div>

        <!-- Main Two-Column Layout -->
        <div class="search-flydown-main">
          <div class="search-flydown-left">
            <!-- Popular Suggestions Section - shows fallback popular searches when no results -->
            <div class="search-flydown-section">
              <h3 class="search-flydown-section-title">POPULAR SUGGESTIONS</h3>
              <div class="search-flydown--popular" data-search-popular>
                <ul class="popular-list">
                  ${popularSearches
                    .map(
                      (search, index) =>
                        `<li id="predictive-search-option-popular-${
                          index + 1
                        }" role="option" aria-selected="false">
                          <a href="/search?q=${encodeURIComponent(
                            search
                          )}" tabindex="-1">
                            ${search}
                          </a>
                        </li>`
                    )
                    .join("")}
                </ul>
              </div>
            </div>
          </div>

          <div class="search-flydown-right">
            <!-- Products Section -->
            <div class="search-flydown-section">
              <h3 class="search-flydown-section-title">PRODUCTS</h3>
              <div class="search-flydown--recommendations" data-search-recommendations>
                <div class="search-flydown-no-results-message">
                  <p>No results found. Try one of the popular suggestions.</p>
                </div>
              </div>
              <span class="predictive-search-status visually-hidden" role="status" aria-hidden="true"></span>
            </div>
          </div>
        </div>
      </div>
    `;

    return popularHTML;
  }

  renderSearchResults(resultsMarkup) {
    // Check if the results are empty by parsing the markup
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = resultsMarkup;

    // Check if predictive search was performed and has results
    const searchResultsWrapper = tempDiv.querySelector(
      "#predictive-search-results-wrapper"
    );
    const hasProducts = tempDiv.querySelector(
      "#predictive-search-results-products-list li"
    );
    const hasQueries = tempDiv.querySelector(
      'li[id*="predictive-search-option-query-"]'
    );
    const hasCollections = tempDiv.querySelector(
      'li[id*="predictive-search-option-collection-"]'
    );

    const hasAnyResults = hasProducts || hasQueries || hasCollections;

    if (!hasAnyResults && searchResultsWrapper) {
      // No results found - check if we should show popular suggestions from settings
      const { trending = [], popularSearches = [] } =
        window.themeSearchData || {};

      if (popularSearches.length > 0) {
        // Show popular suggestions fallback
        const popularHTML = this.renderPopularSuggestions(popularSearches);
        this.predictiveSearchResults.innerHTML = popularHTML;
        this.setAttribute("results", true);

        // Handle trending keywords after rendering popular suggestions
        this.renderTrendingKeywords(trending);
      } else {
        // No popular suggestions configured, show the original "no results" state from server
        this.predictiveSearchResults.innerHTML = resultsMarkup;
        this.setAttribute("results", true);
        // Handle trending keywords after rendering search results
        this.renderTrendingKeywords(trending);
      }
    } else {
      // Has results, show search results
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.setAttribute("results", true);

      const hasQueries = tempDiv.querySelector(
        'li[id*="predictive-search-option-query-"]'
      );

      const predictiveSearchProductGrid = this.querySelector('.search-flydown-main');

      if (!hasQueries) {
        predictiveSearchProductGrid.classList.add("predictive-search-no-queries");
      }
      else
      {
        predictiveSearchProductGrid.classList.remove("predictive-search-no-queries");
      }

      // Initialize ProductGridItem for dynamically added search result products
      this.initProductGridItems();

      // Handle trending keywords after rendering search results
      // const { trending = [] } = window.themeSearchData || {};
      // this.renderTrendingKeywords(trending);
    }

    this.open();
  }

  initProductGridItems() {
    if (!window.ProductGridItem_ProductGridItem) return;

    const productItems = this.querySelectorAll(
      "#predictive-search-results-products-list [data-product-item]"
    );

    // Find the parent Shopify section ID so the modal selector works correctly
    const sectionEl = productItems[0]?.closest('[id^="shopify-section-"]');
    const sectionId = sectionEl
      ? sectionEl.id.replace("shopify-section-", "")
      : "header";

    productItems.forEach((productItem) => {
      const productDataEl = productItem.querySelector("[data-product-data]");
      if (!productDataEl) return;

      try {
        const productData = JSON.parse(productDataEl.innerHTML);
        new window.ProductGridItem_ProductGridItem({
          $el: jQuery(productItem),
          id: sectionId,
          product: productData,
          lazy: true,
        });
      } catch (e) {
        console.error("Error initializing ProductGridItem in search:", e);
      }
    });
  }

  showDefaultContent() {
    // Restore the original default content
    this.predictiveSearchResults.innerHTML = this.defaultContent;
    const { trending = [] } = window.themeSearchData || {};
    if (trending.length > 0) {
      this.renderTrendingKeywords(trending);
    }
    this.removeAttribute("results");

    // Initialize quick view for pre-defined product list
    this.initProductGridItems();
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector(".section-header")?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    // Update position for fixed positioning
    this.updatePosition();

    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;

    // Add CSS classes to make flydown visible
    this.classList.add("live-search--active");
    this.predictiveSearchResults.classList.add("active");
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");

    // Remove CSS classes to hide flydown
    this.classList.remove("live-search--active");
    this.predictiveSearchResults.classList.remove("active");
  }
}

customElements.define("predictive-search", PredictiveSearch);
